// client/src/v2/components/drawer/AllocationPanel.jsx

import React, { useMemo, useState } from "react";
import WormList from "./stats/WormList";
import {
  firstExamFromHistory,
  firstExamSentence,
  formatOversubscriptionFactor,
  oversubscriptionFromHistory,
  oversubscriptionNote,
  oversubscriptionSentence,
} from "./oversubscription";
import InfoTip from "../InfoTip";

/** How many recent entry years to offer as tabs (only years with real rows). */
const MAX_YEAR_TABS = 3;

/** A row appears only when that figure is stored. */
const ALLOCATION_CARD_FIELDS = [
  {
    key: "total_applications",
    label: "Applications",
    tip: "Published applications or applicants for this school. This can include children who did not sit the entrance test.",
  },
  {
    key: "admissions_offered",
    label: "Offers",
    tip: "Places offered. This can differ from the published admission number.",
  },
  {
    key: "cutoff_mark",
    label: "Cut-off",
    tip: "Published qualifying score, rank, or furthest distance for this allocation.",
  },
  {
    key: "cleared_stage1",
    label: "Reached stage 2",
    tip: "Children invited to, or who sat, the second stage when the school publishes that count.",
  },
  {
    key: "selective_places",
    label: "Selective places",
    tip: "Selective places in the intake when the school publishes that figure separately.",
  },
];

const LA_SLUG_LABELS = {
  buckinghamshire: "Buckinghamshire Council",
  dfe: "Department for Education",
  sutton: "London Borough of Sutton",
  trafford: "Trafford Council",
  wirral: "Wirral Council",
  warwickshire: "Warwickshire County Council",
  bcp: "Bournemouth, Christchurch and Poole Council",
};

function partitionLineItems(raw) {
  const tables = [];
  const lines = [];
  if (!Array.isArray(raw)) return { tables, lines };
  for (const item of raw) {
    if (
      item &&
      typeof item === "object" &&
      item.kind === "table" &&
      Array.isArray(item.columns) &&
      Array.isArray(item.rows)
    ) {
      tables.push(item);
    } else if (typeof item === "string" && item.trim()) {
      lines.push(item);
    } else if (item && item.text != null && String(item.text).trim()) {
      lines.push(String(item.text));
    }
  }
  return { tables, lines };
}

function allocationCellClass(columns, index) {
  if (index === 0) return "v2-allocation-table__label";
  const heading = String(columns[index] || "");
  if (/last|reached|offered/i.test(heading)) return "v2-allocation-table__text";
  return "v2-allocation-table__num";
}

const COUNT_HEADER = /^(1st|2nd|3rd|4th|offers?|places?|applications?|total preferences|higher preference|refusals)$/i;
const SKIP_HEADER = /mile|score|rank|distance|qualifying|waiting|minimum|last|furthest|reached/i;

function parseCount(value) {
  if (value == null) return null;
  const text = String(value).trim().replace(/,/g, "");
  if (!/^\d+(?:\.\d+)?$/.test(text)) return null;
  return Number(text);
}

/** A bar only when the columns are counts of the same kind. Miles and scores stay in the table. */
function allocationChart(table) {
  const columns = table.columns || [];
  const countCols = columns
    .map((col, index) => ({ label: String(col || "").trim(), index }))
    .filter((col) => col.index > 0 && COUNT_HEADER.test(col.label) && !SKIP_HEADER.test(col.label));
  if (countCols.length < 1) return null;
  const rows = Array.isArray(table.rows) ? table.rows : [];
  const first = rows[0];
  if (!first) return null;
  const yearLike = /^\d{4}$/.test(String(first[0] || "").trim());
  if (yearLike && countCols.length >= 2) {
    const items = countCols
      .map((col) => ({ key: col.label, label: col.label, value: parseCount(first[col.index]) }))
      .filter((item) => item.value != null);
    if (items.length < 2) return null;
    return {
      title: `${first[0]} figures`,
      hint: "Latest year in the table. Each bar is beside the largest figure in this row.",
      items,
    };
  }
  const prefer = countCols.find((col) => /^offers?$/i.test(col.label)) || countCols[0];
  const items = rows
    .map((row, index) => ({
      key: `${index}-${row?.[0] || ""}`,
      label: String(row?.[0] || "—"),
      value: parseCount(row?.[prefer.index]),
    }))
    .filter((item) => item.value != null && item.label !== "—");
  if (items.length < 2 || items.length > 8) return null;
  return {
    title: prefer.label,
    hint: "Each bar is beside the largest figure in this column.",
    items,
  };
}

function AllocationTable({ table }) {
  const columns = table.columns || [];
  const chart = allocationChart(table);
  return (
    <div className="v2-allocation-table-block">
      {table.title ? <div className="v2-allocation-table-title">{table.title}</div> : null}
      {chart ? <WormList title={chart.title} hint={chart.hint} items={chart.items} /> : null}
      <div className="v2-allocation-table-wrap">
        <table className="v2-allocation-table">
          <thead>
            <tr>
              {columns.map((col, i) => (
                <th key={i} className={allocationCellClass(columns, i)}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, r) => (
              <tr key={r}>
                {columns.map((_, c) => {
                  const value = row?.[c];
                  const empty = value == null || String(value).trim() === "";
                  return (
                    <td key={c} className={allocationCellClass(columns, c)}>
                      {empty ? "—" : value}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {table.caption ? <p className="v2-allocation-table-caption">{table.caption}</p> : null}
    </div>
  );
}

/** Offer-day cut-offs + Qualifying marks first; PAN / ranking / notes after. */
function orderLineItems(lines) {
  const primary = [];
  const marks = [];
  const rest = [];
  for (const line of lines) {
    if (/National Offer Day|last rank offered|furthest distance offered/i.test(line)) {
      primary.push(line);
    } else if (/^Qualifying marks/i.test(line)) {
      marks.push(line);
    } else {
      rest.push(line);
    }
  }
  return [...primary, ...marks, ...rest];
}

function isPublishedCutoffsRow(r) {
  return r?.round_code === "published_cutoffs" || r?.la_slug === "published_cutoffs";
}

function isDfePreferencesRow(r) {
  return r?.round_code === "dfe_preferences" || r?.la_slug === "dfe";
}

function roundTitleFor(r) {
  const label = (r.round_label && String(r.round_label).trim()) || "";
  if (isPublishedCutoffsRow(r)) return label || "Published cut-offs";
  return (
    label ||
    ({
      march_national: "National offer (March)",
      realloc_april: "Re-allocation (spring)",
      realloc_may: "Second round (late spring)",
    }[r.round_code] ||
      r.round_code)
  );
}

function sourceLinkLabel(url) {
  const u = (url || "").toLowerCase();
  if (u.includes(".pdf") || u.includes("/media/")) return "Open published summary →";
  if (u.includes(".xlsx") || u.includes(".xls") || u.includes("spreadsheet")) {
    return "Open allocation spreadsheet →";
  }
  return "Open source →";
}

/** Snapshot keys that usually duplicate history line_items — skip when history is shown. */
const SNAPSHOT_KEYS_COVERED_BY_HISTORY = new Set([
  "admissions_offered",
  "cutoff_mark",
  "selective_places",
]);

function policyIsTwoStage(policy) {
  let stages = policy?.exam_stages;
  if (typeof stages === "string") {
    try {
      stages = JSON.parse(stages);
    } catch {
      stages = null;
    }
  }
  if (stages && !Array.isArray(stages) && Array.isArray(stages.stages)) stages = stages.stages;
  if (Array.isArray(stages)) {
    const second = stages.some((item) => {
      if (!item || typeof item !== "object") return false;
      const stage = Number(item.stage ?? item.stage_number ?? item.stage_no);
      return Number.isFinite(stage) && stage >= 2;
    });
    if (second) return true;
  }
  return /stage\s*2|second stage|round two/i.test(String(policy?.exam_assessment_type || ""));
}

function OversubscriptionList({ items, exams, twoStage }) {
  const examByYear = new Map((exams || []).map((exam) => [exam.year, exam]));
  const rows = [];
  const seen = new Set();
  for (const item of items || []) {
    rows.push({ item, exam: examByYear.get(item.year) || null });
    if (item.year != null) seen.add(item.year);
  }
  const latestPreference = (items || []).find((item) => item.year != null)?.year ?? 0;
  for (const exam of exams || []) {
    if (seen.has(exam.year) || exam.year < latestPreference - 4) continue;
    rows.push({ item: null, exam });
    seen.add(exam.year);
  }
  rows.sort((a, b) => (b.item?.year || b.exam?.year || 0) - (a.item?.year || a.exam?.year || 0));
  if (!rows.length) return null;
  const links = [];
  const seenSource = new Set();
  for (const exam of exams || []) {
    if (!exam.source || seenSource.has(exam.source)) continue;
    seenSource.add(exam.source);
    links.push({ href: exam.source, label: exam.sourceLabel || "School exam figures" });
  }
  const examGate = (exams || []).find((exam) => exam.gate)?.gate || ((exams || []).length && twoStage ? "stage" : null);
  const note = oversubscriptionNote(items || [], {
    twoStage,
    examGate,
    examHasPlaces: (exams || []).some((exam) => exam.places),
  });
  return (
    <section className="ds-oversub" aria-label="Oversubscribed">
      <h4 className="ds-oversub-title">
        Oversubscribed
        <InfoTip label="About oversubscribed" text={note} />
      </h4>
      <ul className="ds-oversub-list">
        {rows.map(({ item, exam }) => (
          <li className="ds-oversub-row" key={`${item?.year || exam?.year}-${item?.kind || "exam"}`}>
            <span className="ds-oversub-year">{item?.year || exam?.year || ""}</span>
            <div className="ds-oversub-lines">
              {item ? (
                <div className="ds-oversub-line">
                  <strong className="ds-oversub-factor">
                    {formatOversubscriptionFactor(item.applications, item.offers)}
                  </strong>
                  <span className="ds-oversub-detail">{oversubscriptionSentence(item)}</span>
                </div>
              ) : null}
              {exam ? (
                <div className="ds-oversub-line">
                  <strong className="ds-oversub-factor">
                    {exam.places ? formatOversubscriptionFactor(exam.satStage1, exam.places) : ""}
                  </strong>
                  <span className="ds-oversub-detail">{firstExamSentence(exam)}</span>
                </div>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
      {links.length ? (
        <p className="ds-oversub-note">
          {links.map((link, index) => (
            <React.Fragment key={link.href}>
              {index > 0 ? " " : null}
              <a className="v2-drawer-link" href={link.href} target="_blank" rel="noreferrer">
                {link.label} →
              </a>
            </React.Fragment>
          ))}
        </p>
      ) : null}
    </section>
  );
}

function YearTabs({ years, active, onSelect }) {
  if (!years || years.length <= 1) return null;
  return (
    <div className="v2-catchment-year-toggles v2-allocation-history-year-tabs">
      {years.map((y) => (
        <button
          key={y}
          type="button"
          className={`v2-catchment-year-btn ${y === active ? "v2-catchment-year-btn--active" : ""}`}
          onClick={() => onSelect(y)}
        >
          {y}
        </button>
      ))}
    </div>
  );
}

function AllocationHistoryBlock({ yearRows }) {
  if (!yearRows?.length) return null;
  const first = yearRows[0];
  const published = yearRows.every(isPublishedCutoffsRow);
  const dfe = yearRows.every(isDfePreferencesRow);
  const laLabel =
    !published && !dfe && first?.la_slug && LA_SLUG_LABELS[first.la_slug]
      ? LA_SLUG_LABELS[first.la_slug]
      : null;

  return (
    <section className="v2-catchment-box v2-allocation-history-block">
      <h4 className="v2-catchment-box__title">
        {published ? "Published cut-offs" : dfe ? "Offer day figures" : "Council allocation"}
      </h4>
      <div className="v2-catchment-scroll">
      <p className="v2-muted v2-allocation-history-lead">
        {published
          ? "Offer-day ranks, distances, and qualifying standards from LA or school publications."
          : dfe
            ? "Department for Education figures for national offer day. 1st, 2nd and 3rd are preferences, not a total of applications."
            : "How places were allocated for qualified selective applicants, from the local authority’s published allocation files."}
        {laLabel ? (
          <>
            {" "}
            ({laLabel})
          </>
        ) : null}
      </p>

      {yearRows.map((r, idx) => {
        const { tables, lines: rawLines } = partitionLineItems(r.line_items);
        const ordered = orderLineItems(rawLines);
        const leadLines = ordered.filter((line) => /^Qualifying marks/i.test(line));
        const restLines = ordered.filter((line) => !/^Qualifying marks/i.test(line));
        const statsUrl = r.statistics_page_url;
        const sourceUrl = r.data_source_url;
        const showStats =
          statsUrl && sourceUrl && String(statsUrl).trim() !== String(sourceUrl).trim();
        const heading = (r.profile_heading || "").trim();
        // Skip boilerplate heading that restates the section title.
        const showHeading =
          heading &&
          !published &&
          !/offer-day cut-offs/i.test(heading);

        return (
          <div
            key={`${r.entry_year}-${r.round_code}-${r.round_order || 0}`}
            className={`v2-allocation-round-block${idx === 0 ? " v2-allocation-round-block--first" : ""}`}
          >
            {(!published && !dfe) || yearRows.length > 1 ? (
              <div className="v2-allocation-round-title">{roundTitleFor(r)}</div>
            ) : null}
            {showHeading ? (
              <p className="v2-muted v2-allocation-profile-heading">{heading}</p>
            ) : null}
            {leadLines.length ? (
              <ul className="v2-allocation-line-items">
                {leadLines.map((line, i) => (
                  <li key={`lead-${i}`}>{line}</li>
                ))}
              </ul>
            ) : null}
            {tables
              .filter((table) => table.role !== "first_exam")
              .map((table, i) => (
                <AllocationTable key={`table-${i}`} table={table} />
              ))}
            {restLines.length ? (
              <ul className="v2-allocation-line-items">
                {restLines.map((line, i) => (
                  <li key={`rest-${i}`}>{line}</li>
                ))}
              </ul>
            ) : null}
            {!leadLines.length && !tables.length && !restLines.length ? (
              <p className="v2-muted">No published lines for this round.</p>
            ) : null}
            {sourceUrl ? (
              <div className="v2-detail-row v2-allocation-source-row">
                <span className="v2-detail-label">Source</span>
                <span className="v2-detail-value">
                  <a className="v2-drawer-link" href={sourceUrl} target="_blank" rel="noreferrer">
                    {sourceLinkLabel(sourceUrl)}
                  </a>
                </span>
              </div>
            ) : null}
            {showStats ? (
              <div className="v2-detail-row">
                <span className="v2-detail-label">Statistics index</span>
                <span className="v2-detail-value">
                  <a className="v2-drawer-link" href={statsUrl} target="_blank" rel="noreferrer">
                    Allocation statistics →
                  </a>
                </span>
              </div>
            ) : null}
          </div>
        );
      })}
      </div>
    </section>
  );
}

function allocationCardValue(snapshot, key) {
  const value = snapshot?.[key];
  if (value == null || String(value).trim() === "") return "";
  const text = String(value);
  // An estimate is not a published figure.
  if (/\(est\.?\)/i.test(text) || /^\s*~/.test(text)) return "";
  return text;
}

function allocationCardRows(snapshot, hideCovered) {
  const rows = ALLOCATION_CARD_FIELDS
    .map((field) => ({ ...field, value: allocationCardValue(snapshot, field.key) }))
    .filter((field) => field.value && !(hideCovered && SNAPSHOT_KEYS_COVERED_BY_HISTORY.has(field.key)));
  // Offers on their own repeat the admission number. That is not an allocation result.
  if (rows.length === 1 && rows[0].key === "admissions_offered") return [];
  return rows;
}

function historyByYearMap(historyList) {
  const grouped = new Map();
  for (const row of historyList) {
    const year = Number(row.entry_year);
    if (!Number.isFinite(year)) continue;
    if (!grouped.has(year)) grouped.set(year, []);
    grouped.get(year).push(row);
  }
  for (const rows of grouped.values()) {
    rows.sort((a, b) => (Number(a.round_order) || 0) - (Number(b.round_order) || 0));
  }
  return grouped;
}

/** One allocation card for every school. Published tables stay with that year. */
function AllocationCardView({ policies, allocationHistory }) {
  const list = Array.isArray(policies) ? policies : [];
  const historyByYear = useMemo(
    () => historyByYearMap(Array.isArray(allocationHistory) ? allocationHistory : []),
    [allocationHistory]
  );
  const years = useMemo(() => {
    const set = new Set();
    for (const policy of list) {
      const year = Number(policy?.entry_year);
      if (!Number.isFinite(year)) continue;
      if (allocationCardRows(policy?.allocation_snapshot, historyByYear.has(year)).length) set.add(year);
    }
    for (const year of historyByYear.keys()) set.add(year);
    return [...set].sort((a, b) => b - a).slice(0, MAX_YEAR_TABS);
  }, [list, historyByYear]);

  const historyList = Array.isArray(allocationHistory) ? allocationHistory : [];
  const factors = useMemo(() => oversubscriptionFromHistory(historyList), [historyList]);
  const exams = useMemo(() => firstExamFromHistory(historyList), [historyList]);
  const twoStage = list.some(policyIsTwoStage);
  const [selectedYear, setSelectedYear] = useState(null);
  const year = selectedYear != null && years.includes(selectedYear) ? selectedYear : years[0] ?? null;
  const snapshot = list.find((policy) => Number(policy?.entry_year) === year)?.allocation_snapshot;
  const yearHistory = year != null ? historyByYear.get(year) || [] : [];
  const rows = allocationCardRows(snapshot, yearHistory.length > 0);

  if (!years.length) {
    return (
      <div className="v2-catchment-panel">
        <section className="v2-catchment-box v2-catchment-box--empty">
          Data will be available soon.
        </section>
      </div>
    );
  }

  return (
    <div className="v2-catchment-panel">
      <OversubscriptionList items={factors} exams={exams} twoStage={twoStage} />
      <YearTabs years={years} active={year} onSelect={setSelectedYear} />
      <div className={`v2-catchment-content${years.length > 1 ? "" : " v2-catchment-content--flush"}`}>
        <div className="v2-catchment-standard">
          {rows.length ? (
            <section className="v2-catchment-box">
              <h4 className="v2-catchment-box__title">Allocation</h4>
              <div className="v2-catchment-scroll">
                <div className="ds-tiles">
                  {rows.map((field) => (
                    <div
                      className={`ds-tile${String(field.value).length > 28 ? " ds-tile--wide" : ""}`}
                      key={field.key}
                    >
                      <span className="ds-tile-label">
                        {field.label}
                        {field.tip ? <InfoTip text={field.tip} label={`About ${field.label}`} /> : null}
                      </span>
                      <strong>{field.value}</strong>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          ) : null}
          {yearHistory.length ? <AllocationHistoryBlock yearRows={yearHistory} /> : null}
          {!rows.length && !yearHistory.length ? (
            <section className="v2-catchment-box v2-catchment-box--empty">
              Data will be available soon.
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * 11+ allocation for every school. A figure is shown only when it is stored.
 * Published cut-off tables stay in their own card for that year.
 */
export default function AllocationPanel({ policies, allocationHistory, loading, error }) {
  if (loading) {
    return (
      <div className="v2-catchment-panel">
        <div className="v2-muted">Loading allocation details…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="v2-catchment-panel">
        <div className="v2-muted" style={{ color: "#c00" }}>
          {error}
        </div>
      </div>
    );
  }

  return <AllocationCardView policies={policies} allocationHistory={allocationHistory} />;
}
